import React, { useState, useRef, useEffect } from "react";
import styles from "./ListFilter.module.css";
import { FaChevronDown } from "react-icons/fa";

// Helper function to clean empty arrays from filters
const cleanFilters = (filters) => {
  const cleaned = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

function ListFilter({
  filterOptions,
  setFilterOptions,
  handleSearch,
  setCheckedValue,
  openFilter,
  setOpenFilter,
  metadata,
  searchValue,
  setCurrentPage,
  groupBy = [],
  setGroupBy,
}) {
  const [selectedFilter, setSelectedFilter] = useState("");
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [groupByOpen, setGroupByOpen] = useState(false);
  const [groupByModalPosition, setGroupByModalPosition] = useState({ top: 0, left: 0 });
  const filterButtonRefs = useRef({});
  const groupByButtonRef = useRef(null);

  const currentGroupBy = groupBy || [];

  const getSelectedCount = (facetKey) => {
    const includeCount = filterOptions.include_filters?.[facetKey]?.length || 0;
    const excludeCount = filterOptions.exclude_filters?.[facetKey]?.length || 0;
    const total = includeCount + excludeCount;
    return total > 0 ? `(${total})` : null;
  };

  const getDisplayNameForRequestKey = (requestKey) => {
    const tab = metadata.tabs?.find((t) => t.request_key === requestKey);
    return tab?.display_name || requestKey;
  };

  const updateFacetFilter = (facetKey, value, type) => {
    setFilterOptions((prev) => {
      let includeFilters = { ...prev.include_filters };
      let excludeFilters = { ...prev.exclude_filters };

      const includeArray = includeFilters[facetKey] ? [...includeFilters[facetKey]] : [];
      const excludeArray = excludeFilters[facetKey] ? [...excludeFilters[facetKey]] : [];

      if (type === "include") {
        if (includeArray.includes(value)) {
          includeFilters[facetKey] = includeArray.filter((v) => v !== value);
        } else {
          includeFilters[facetKey] = [...includeArray, value];
          excludeFilters[facetKey] = excludeArray.filter((v) => v !== value);
        }
      } else if (type === "exclude") {
        if (excludeArray.includes(value)) {
          excludeFilters[facetKey] = excludeArray.filter((v) => v !== value);
        } else {
          excludeFilters[facetKey] = [...excludeArray, value];
          includeFilters[facetKey] = includeArray.filter((v) => v !== value);
        }
      }

      return {
        include_filters: cleanFilters(includeFilters),
        exclude_filters: cleanFilters(excludeFilters),
      };
    });

    setCheckedValue(true);
  };

  // Remove a single filter and get the new filters object
  const removeFilter = (facetKey, value, type) => {
    let includeFilters = { ...filterOptions.include_filters };
    let excludeFilters = { ...filterOptions.exclude_filters };

    if (type === "include" && includeFilters[facetKey]) {
      includeFilters[facetKey] = includeFilters[facetKey].filter((v) => v !== value);
    } else if (type === "exclude" && excludeFilters[facetKey]) {
      excludeFilters[facetKey] = excludeFilters[facetKey].filter((v) => v !== value);
    }

    return {
      include_filters: cleanFilters(includeFilters),
      exclude_filters: cleanFilters(excludeFilters),
    };
  };

  const handleChipRemove = (facetKey, value, type) => {
    const newFilters = removeFilter(facetKey, value, type);
    setFilterOptions(newFilters);
    setCurrentPage(1);
    handleSearch(searchValue, 1, true, null, newFilters, currentGroupBy);
  };

  const handleApplyFilters = () => {
    const cleanedFilters = {
      include_filters: cleanFilters(filterOptions.include_filters || {}),
      exclude_filters: cleanFilters(filterOptions.exclude_filters || {}),
    };
    setFilterOptions(cleanedFilters);
    setCurrentPage(1);
    handleSearch(searchValue, 1, true, null, cleanedFilters, currentGroupBy);
    setOpenFilter(false);
    setSelectedFilter("");
  };

  const handleClearFilters = () => {
    setGroupBy([]);
    const clearedFilters = {
      include_filters: {},
      exclude_filters: {},
    };

    setFilterOptions(clearedFilters);
    setCurrentPage(1);
    handleSearch(searchValue, 1, true, null, clearedFilters, []);
    setOpenFilter(false);
    setSelectedFilter("");
  };

  const hasItems = (requestKey) => {
    const filterData = metadata.filters || {};
    const facetKey = requestKey.substring(0, requestKey.length - 1);
    return filterData[facetKey] && Object.keys(filterData[facetKey]).length > 0;
  };

  const handleGroupByToggle = (value) => {
    if (!setGroupBy) return;
    setGroupBy((prev) => {
      const current = prev || [];
      if (current.includes(value)) {
        return current.filter((v) => v !== value);
      } else {
        return [...current, value];
      }
    });
  };

  const handleGroupByApply = () => {
    setGroupByOpen(false);
    setCurrentPage(1);
    handleSearch(searchValue, 1, true, null, filterOptions, currentGroupBy);
  };

  const handleGroupByClear = () => {
    if (setGroupBy) {
      setGroupBy([]);
    }
    setGroupByOpen(false);
    setCurrentPage(1);
    handleSearch(searchValue, 1, true, null, filterOptions, []);
  };

  const handleGroupByButtonClick = () => {
    const button = groupByButtonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    setGroupByModalPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });

    setGroupByOpen(!groupByOpen);
    setOpenFilter(false);
    setSelectedFilter("");
  };

  const handleGroupByChipRemove = (value) => {
    if (!setGroupBy) return;
    const newGroupBy = currentGroupBy.filter((v) => v !== value);
    setGroupBy(newGroupBy);
    setCurrentPage(1);
    handleSearch(searchValue, 1, true, null, filterOptions, newGroupBy);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openFilter &&
        !event.target.closest(`.${styles.modal}`) &&
        !event.target.closest(`.${styles.filterButton}`)
      ) {
        setOpenFilter(false);
        setSelectedFilter("");
      }
      if (
        groupByOpen &&
        !event.target.closest(`.${styles.modal}`) &&
        !event.target.closest(`.${styles.groupByButton}`)
      ) {
        setGroupByOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openFilter, groupByOpen]);

  const renderFilterOptions = () => {
    if (!selectedFilter) return null;

    const facetKey = selectedFilter.request_key.slice(0, -1);
    const facetOptions = metadata.filters?.[facetKey] || {};

    return Object.entries(facetOptions).map(([key, value]) => (
      <div key={key} className={styles.filterOption}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={
              filterOptions.include_filters?.[selectedFilter.request_key]?.includes(key) || false
            }
            onChange={() => updateFacetFilter(selectedFilter.request_key, key, "include")}
          />
          <span
            className={`${styles.checkMark} ${
              filterOptions.include_filters?.[selectedFilter.request_key]?.includes(key)
                ? styles.includeChecked
                : ""
            }`}
          >
            ✓
          </span>
        </label>

        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={
              filterOptions.exclude_filters?.[selectedFilter.request_key]?.includes(key) || false
            }
            onChange={() => updateFacetFilter(selectedFilter.request_key, key, "exclude")}
          />
          <span
            className={`${styles.checkMark} ${
              filterOptions.exclude_filters?.[selectedFilter.request_key]?.includes(key)
                ? styles.excludeChecked
                : ""
            }`}
          >
            ✗
          </span>
        </label>

        <span className={styles.optionText}>
          {key} ({value})
        </span>
      </div>
    ));
  };

  const renderSelectedChips = (type) => {
    const filters =
      type === "include" ? filterOptions.include_filters : filterOptions.exclude_filters;

    if (!filters) return null;

    return Object.entries(filters).flatMap(([facet, values]) =>
      values.map((item, index) => (
        <span
          key={`${type}-${facet}-${index}`}
          className={`${styles.chip} ${type === "exclude" ? styles.excluded : ""}`}
          onClick={() => handleChipRemove(facet, item, type)}
        >
          {item}
          <span className={styles.closeIcon}>×</span>
        </span>
      )),
    );
  };

  const renderGroupByChips = () => {
    if (currentGroupBy.length === 0) return null;

    return currentGroupBy.map((item, index) => (
      <span
        key={`groupby-${index}`}
        className={`${styles.chip} ${styles.groupByChip}`}
        onClick={() => handleGroupByChipRemove(item)}
      >
        {getDisplayNameForRequestKey(item)}
        <span className={styles.closeIcon}>×</span>
      </span>
    ));
  };

  const handleButtonClick = (item, index) => {
    if (!hasItems(item.request_key)) return;

    const button = filterButtonRefs.current[index];
    if (!button) return;

    const rect = button.getBoundingClientRect();
    setModalPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });

    if (selectedFilter && selectedFilter.request_key === item.request_key) {
      setOpenFilter(false);
      setSelectedFilter("");
    } else {
      setOpenFilter(true);
      setSelectedFilter(item);
      setGroupByOpen(false);
    }
  };

  const hasActiveFilters = () => {
    const includes = filterOptions.include_filters || {};
    const excludes = filterOptions.exclude_filters || {};

    return (
      Object.values(includes).some((arr) => arr.length > 0) ||
      Object.values(excludes).some((arr) => arr.length > 0)
    );
  };

  const getGroupByOptions = () => {
    return (
      metadata.tabs?.map((tab) => ({
        value: tab.request_key,
        label: tab.display_name,
      })) || []
    );
  };

  return (
    <div className={styles.container}>
      {/* Filter Buttons Row */}
      <div className={styles.filterHeader}>
        <span className={styles.filterText}>Filter</span>
        <div className={styles.filterButtons}>
          {metadata.tabs.map((item, index) => {
            const count = getSelectedCount(item.request_key);
            const isEmpty = !hasItems(item.request_key);

            return (
              <button
                key={item.display_name}
                ref={(el) => (filterButtonRefs.current[index] = el)}
                className={`${styles.filterButton} ${isEmpty ? styles.disabled : ""}`}
                disabled={isEmpty}
                onClick={() => handleButtonClick(item, index)}
              >
                {item.display_name} {count}
                <FaChevronDown />
              </button>
            );
          })}

          {/* <button
            ref={groupByButtonRef}
            className={`${styles.filterButton} ${styles.groupByButton}`}
            onClick={handleGroupByButtonClick}
          >
            Group By {currentGroupBy.length > 0 ? `(${currentGroupBy.length})` : ""}
            <FaChevronDown />
          </button> */}
        </div>
      </div>
      <div className={styles.groupBySection}>
        <span className={styles.groupByLabel}>Group By:</span>
        <div className={styles.groupByCheckboxes}>
          {metadata.tabs?.map((tab) => {
            const isEmpty = !hasItems(tab.request_key);

            return (
              <label
                key={tab.request_key}
                className={`${styles.groupByCheckboxLabel} ${isEmpty ? styles.disabled : ""}`}
              >
                <input
                  type="checkbox"
                  disabled={isEmpty}
                  checked={currentGroupBy.includes(tab.request_key)}
                  onChange={() => {
                    if (isEmpty) return;

                    const newGroupBy = currentGroupBy.includes(tab.request_key)
                      ? currentGroupBy.filter((v) => v !== tab.request_key)
                      : [...currentGroupBy, tab.request_key];

                    setGroupBy(newGroupBy);
                    setCurrentPage(1);
                    handleSearch(searchValue, 1, true, null, filterOptions, newGroupBy);
                  }}
                />
                <span className={styles.groupByCheckboxText}>{tab.display_name}</span>
              </label>
            );
          })}
        </div>
      </div>

      {(hasActiveFilters() || currentGroupBy.length > 0) && (
        <div className={styles.selectedFilters}>
          {filterOptions.include_filters &&
            Object.values(filterOptions.include_filters).some((arr) => arr.length > 0) && (
              <div className={styles.chipGroup}>
                <strong>Selected:</strong>
                {renderSelectedChips("include")}
              </div>
            )}

          {filterOptions.exclude_filters &&
            Object.values(filterOptions.exclude_filters).some((arr) => arr.length > 0) && (
              <div className={styles.chipGroup}>
                <strong>Excluded:</strong>
                {renderSelectedChips("exclude")}
              </div>
            )}

          {currentGroupBy.length > 0 && (
            <div className={styles.chipGroup}>
              <strong>Grouped By:</strong>
              {renderGroupByChips()}
            </div>
          )}

          <button className={styles.clearAllButton} onClick={handleClearFilters}>
            Clear All
          </button>
        </div>
      )}

      {openFilter && selectedFilter && (
        <div
          className={styles.modal}
          style={{
            position: "absolute",
            top: `${modalPosition.top}px`,
            left: `${modalPosition.left}px`,
            zIndex: 9999,
            minWidth: "250px",
          }}
        >
          <div className={styles.modalHeader}>
            <span></span>
            <button
              className={styles.closeButton}
              onClick={() => {
                setOpenFilter(false);
                setSelectedFilter("");
              }}
            >
              ×
            </button>
          </div>

          {/* + and - Icons at Top */}
          <div className={styles.iconRow}>
            <span className={styles.iconLabel}>+</span>
            <span className={styles.iconLabel}>−</span>
          </div>

          <div className={styles.modalContent}>{renderFilterOptions()}</div>

          <div className={styles.modalFooter}>
            <button className={styles.applyButton} onClick={handleApplyFilters}>
              Apply
            </button>
          </div>
        </div>
      )}

      {groupByOpen && (
        <div
          className={styles.modal}
          style={{
            position: "absolute",
            top: `${groupByModalPosition.top}px`,
            left: `${groupByModalPosition.left}px`,
            zIndex: 9999,
            minWidth: "200px",
          }}
        >
          <div className={styles.modalHeader}>
            <span>Group By</span>
            <button className={styles.closeButton} onClick={() => setGroupByOpen(false)}>
              ×
            </button>
          </div>

          <div className={styles.modalContent}>
            {getGroupByOptions().map((option) => (
              <div key={option.value} className={styles.filterOption}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={currentGroupBy.includes(option.value)}
                    onChange={() => handleGroupByToggle(option.value)}
                  />
                  <span
                    className={`${styles.checkMark} ${
                      currentGroupBy.includes(option.value) ? styles.includeChecked : ""
                    }`}
                  >
                    ✓
                  </span>
                </label>
                <span className={styles.optionText}>{option.label}</span>
              </div>
            ))}
          </div>

          <div className={styles.modalFooter}>
            <button className={styles.clearButton} onClick={handleGroupByClear}>
              Clear
            </button>
            <button className={styles.applyButton} onClick={handleGroupByApply}>
              Apply
            </button>
          </div>
        </div>
      )}

      {/* style 1 */}
      {/* Group By Toggle Buttons */}
      {/* <div className={styles.groupBySection}>
        <span className={styles.groupByLabel}>Group By:</span>
        <div className={styles.groupByToggleButtons}>
          {metadata.tabs?.map((tab) => (
            <button
              key={tab.request_key}
              className={`${styles.groupByToggle} ${
                currentGroupBy.includes(tab.request_key) ? styles.groupByToggleActive : ""
              }`}
              onClick={() => {
                const newGroupBy = currentGroupBy.includes(tab.request_key)
                  ? currentGroupBy.filter((v) => v !== tab.request_key)
                  : [...currentGroupBy, tab.request_key];
                setGroupBy(newGroupBy);
                setCurrentPage(1);
                handleSearch(searchValue, 1, true, null, filterOptions, newGroupBy);
              }}
            >
              {tab.display_name}
            </button>
          ))}
        </div>
      </div> */}

      {/* style 2 */}
      {/* Group By Checkboxes Inline */}
      {/* <div className={styles.groupBySection}>
        <span className={styles.groupByLabel}>Group By:</span>
        <div className={styles.groupByCheckboxes}>
          {metadata.tabs?.map((tab) => (
            <label key={tab.request_key} className={styles.groupByCheckboxLabel}>
              <input
                type="checkbox"
                checked={currentGroupBy.includes(tab.request_key)}
                onChange={() => {
                  const newGroupBy = currentGroupBy.includes(tab.request_key)
                    ? currentGroupBy.filter((v) => v !== tab.request_key)
                    : [...currentGroupBy, tab.request_key];
                  setGroupBy(newGroupBy);
                  setCurrentPage(1);
                  handleSearch(searchValue, 1, true, null, filterOptions, newGroupBy);
                }}
              />
              <span className={styles.groupByCheckboxText}>{tab.display_name}</span>
            </label>
          ))}
        </div>
      </div> */}

      {/* style 3 */}
      {/* Group By Switches */}
      {/* <div className={styles.groupBySection}>
        <span className={styles.groupByLabel}>Group By:</span>
        <div className={styles.groupBySwitches}>
          {metadata.tabs?.map((tab) => (
            <div
              key={tab.request_key}
              className={styles.groupBySwitchItem}
              onClick={() => {
                const newGroupBy = currentGroupBy.includes(tab.request_key)
                  ? currentGroupBy.filter((v) => v !== tab.request_key)
                  : [...currentGroupBy, tab.request_key];
                setGroupBy(newGroupBy);
                setCurrentPage(1);
                handleSearch(searchValue, 1, true, null, filterOptions, newGroupBy);
              }}
            >
              <span className={styles.groupBySwitchText}>{tab.display_name}</span>
              <div
                className={`${styles.switch} ${
                  currentGroupBy.includes(tab.request_key) ? styles.switchOn : ""
                }`}
              >
                <div className={styles.switchThumb}></div>
              </div>
            </div>
          ))}
        </div>
      </div> */}
    </div>
  );
}

export default ListFilter;